import { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import { Grid, Card, CardHeader, CardContent, Divider, useTheme, Button, CardActions, IconButton, Box } from "@mui/material";

import { usePageData } from "../../../contexts/PageData";

import { BasicFlex } from "../../../utils/Common";

import { capitalize } from "lodash";

import { useNavigate } from "react-router-dom";

import SimpleChip from "../../../utils/utilities/SimpleChip";

import MultiStatusChip from "../../../utils/utilities/MultiStatusChip";

import { formatCurrency, formatGenericDate, formatGenericDateTime, formatPhoneNumber, isValidEmail, convertToFormData } from "../../../utils/util";

import { CardBlock, CardBlockHeader, CardBlockContainer, CardSpacer } from "../../../utils/utilities/CardComponents";

import { DateTime } from "luxon";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { editTask } from "../apiService";

import TimelineWithComments from "../../../utils/utilities/Timeline/TimelineWithComments";

import TimelineComponent from "../../../utils/utilities/Timeline";

import useFollowUpSnackbarStore from "../../../store/repair_follow_up";

import ImageDisplayComponent from "../../../utils/utilities/ImageComponent/ImageDisplay";

import DownloadButton from "../../../utils/utilities/Buttons/DownloadButton";
import EmailButton from "../../../utils/utilities/Buttons/EmailButton";
import EmailField from "../../../utils/utilities/Buttons/EmailField";

import RepairTask from "../Components/RepairTask";

import ShareCard from "../../../utils/utilities/Buttons/ShareCard";

import EditTasks from "../Components/EditTasks";

import EditTask from "../Components/EditTask";

// icons
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HouseRoundedIcon from '@mui/icons-material/HouseRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import GpsFixedRoundedIcon from '@mui/icons-material/GpsFixedRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PinDropRoundedIcon from '@mui/icons-material/PinDropRounded';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import StyleRoundedIcon from '@mui/icons-material/StyleRounded';

const ListHeader = styled.div`
  margin: 2rem 0;
  background-color: ${props => props.theme.colors.lightBg};
  padding: .5rem 1rem;
  border-radius: 5px;
  border: 1px solid ${props => props.theme.colors.border};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
` 

const Details = styled.div`
  max-width: 30rem;
  font-style: italic;
  font-weight: 500;
  font-size: 1.5rem;
  line-height: 1.4;
  /* margin: 1rem 0; */
  background-color: ${props => props.theme.colors.lightBg};
  padding: .5rem 1rem;
  border-radius: 5px;
  /* border: 1px solid ${props => props.theme.colors.border}; */
`

const NoDetails = styled.div`
  font-style: italic;
  font-weight: 500;
  color: ${props => props.theme.colors.lightText};
  line-height: 1.4;
  margin-top: .5rem;
  /* margin: 1rem 0; */
  /* background-color: ${props => props.theme.colors.lightBg}; */
  border-radius: 5px;
  /* border: 1px solid ${props => props.theme.colors.border}; */
`


const getSelectedRepairEvents = (repair = {}) => {
  let events = [];
  if (repair.accepted_at) events.push({username: repair.accepted_by, timestamp: repair.accepted_at, action: <BasicFlex justify="flex-start" gap=".5rem"><SimpleChip label={repair.repair_name} /> was Accepted</BasicFlex>, log_id: 1});
  if (repair.started_at) events.push({username: repair.started_by, timestamp: repair.started_at, action: <BasicFlex justify="flex-start" gap=".5rem"><SimpleChip label={repair.repair_name} /> was Started</BasicFlex>, log_id: 2});
  if (repair.completed_at) events.push({username: repair.completed_by, timestamp: repair.completed_at, action: <BasicFlex justify="flex-start" gap=".5rem"><SimpleChip label={repair.repair_name} /> was Completed</BasicFlex>, log_id: 3});
  return events;
}

const StatusChip = ({ repair }) => {
  return (
    <MultiStatusChip value={repair?.status} values={[{value: "scheduled", label: "Scheduled", status: "error"}, {value: "in_progress", label: "Processing", status: "warning"}, {value: "completed", label: "Completed", status: "success"}]} />
  )
}

const SelectedRepair = ({ selectedRepair = {}, ticket = {} }) => {

  const [email, setEmail] = useState(selectedRepair?.vendor_email || "");

  useEffect(() => {
    setEmail(selectedRepair?.vendor_email || "")
  }, [selectedRepair?.vendor_email]);

  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, reset } = useMutation({
    mutationFn: editTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair_tickets'] });
    }
  })

   const submitEdit = (data) => {
    data = convertToFormData(data);
    mutate({ repair_task_id: selectedRepair?.repair_task_id, dataPacket: data });
  }

  let selected_repair_events = getSelectedRepairEvents(selectedRepair);

  const active_repair_details = [
    {
      header: "Specialist",
      info: <SimpleChip label={selectedRepair?.specialist_name || selectedRepair?.vendor_name} />, // selectedRepair?.stylist_name || selectedRepair?.vendor_name,
      icon: <SupportAgentRoundedIcon fontSize="small" color="basic" />,
      spread: "13rem"
    },
    {
      header: "Repair No.",
      info: `${selectedRepair?.repair_order} / ${ticket?.repairs?.length}`,
      icon: <ListAltRoundedIcon fontSize="small" color="basic" />,
      spread: "13rem"
    },
    {
      header: "Price",
      info: selectedRepair?.price,
      dataType: "currency",
      icon: <AttachMoneyIcon fontSize="small" color="basic" />,
      spread: "13rem"
    },
    {
      header: "Duration",
      info: selectedRepair?.estimated_time_to_complete + " days",
      icon: <AccessTimeIcon fontSize="small" color="basic" />,
      spread: "13rem"
    },
    {
      header: "Status",
      info: <StatusChip repair={selectedRepair} />,
      icon: <FactCheckRoundedIcon fontSize="small" color="basic" />,
      spread: "13rem"
    },
    {
      header: "Location",
      info: selectedRepair?.in_house ? "In House" : "External",
      icon: <GpsFixedRoundedIcon fontSize="small" color="basic" />,
      spread: "13rem"
    },
    // {
    //   header: "Priority",
    //   info: capitalize(selectedRepair?.priority),
    // },
    // {
    //   header: "Bill To",
    //   info: capitalize(selectedRepair?.bill_to),
    // }
  ]

  return (
    <Card variant="outlined" sx={{borderColor: "secondary.main"}}>
      <CardHeader title={selectedRepair?.repair_name} action={(
        <BasicFlex>
          <DownloadButton url={`/repair_tickets/generate/slip/${selectedRepair?.repair_task_id}`} title="Generate Ticket Slip" />
          <EmailButton url={`/repair_tickets/generate/slip/${selectedRepair?.repair_task_id}`} disabled={!email || !isValidEmail(email)} email={email} />
          {/* <IconButton color="secondary" size="small"><EditRoundedIcon /></IconButton> */}
          <EditTask task={selectedRepair} saveFunc={submitEdit} loading={isPending} isSuccess={isSuccess} reset={reset} />
        </BasicFlex>
      )} />
      <Box sx={{px: "1rem", pt: "0"}}>
       <EmailField title="Vendor Email" email={email} setEmail={setEmail} disabled={selectedRepair?.in_house} />
      </Box>

       <Divider sx={{my: "1rem", mb: "2rem"}} />
      <CardContent sx={{pt: "0"}}>
       

        <Grid container spacing={3}>
          <Grid size={{xs: 12, md: 6}}>
            <BasicFlex dir="column" gap="1rem">
              <CardBlockContainer>
                <CardBlockHeader>Repair Details</CardBlockHeader>
                
                {active_repair_details.map((block) => (
                  <CardBlock key={block.header} {...block} />
                ))}

                <CardSpacer />
            
                <CardBlockHeader>Instructions</CardBlockHeader>
                {selectedRepair?.details ? <Details>
                  {/* {selectedRepair?.details} */}
                  &ldquo; {selectedRepair?.details} &rdquo;
                </Details> : <NoDetails>No Instructions provided.</NoDetails>}

                <CardSpacer />
                
            </CardBlockContainer>
            </BasicFlex>
          </Grid>

          <Grid size={{xs: 12, md: 6}}>
            <Card variant="outlined">
              <CardHeader subheader="Repair History" />
              <CardContent sx={{pt: 0}}>
                <TimelineComponent resource={"repair_tickets"} resource_id={ticket?.repair_ticket_id} title="Repair Timeline" 
                events={selected_repair_events} manual={true} 
                alternate={true} expand={true} />
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{xs: 12}}>
            <CardBlockHeader>Media</CardBlockHeader>
            {selectedRepair?.media_urls?.length ? <ImageDisplayComponent media_urls={selectedRepair?.media_urls} onClick={() => {}} /> : <NoDetails>No Media</NoDetails>}
          </Grid>

        </Grid>
      </CardContent>
    </Card>
  )
}


const MainRepairView = ({ ticket = {}, dash=false }) => {

  const [activeRepair, setActiveRepair] = useState(ticket?.current_repair_order);

  const selectedRepair = ticket?.repairs?.find(repair => repair?.repair_order === activeRepair);

  const navigate = useNavigate();

  useEffect(() => {
    setActiveRepair(ticket?.current_repair_order);
  }, [ticket])

  const handleActiveRepair = (index) => {
    setActiveRepair(index);
  }

  const showSnackbar = useFollowUpSnackbarStore(state => state.showSnackbar);

  const scheduleFollowUp = (date) => {
    showSnackbar({repair_id: ticket?.repair_ticket_id, repair_num: ticket?.repair_ticket_num, date, customer_id: ticket?.customer_id, customer_name: ticket?.client_name, customer_phone: ticket?.customer_phone});
    navigate(`/calendar?view=timeline&date=${date}`);
  }

  const cust_block = [
    { header: "Customer", info: ticket?.client_name },
    { header: "Phone", info: formatPhoneNumber(ticket?.customer_phone) },
  ]

  const wig_details = [
    { header: "Wig Source", info: capitalize(ticket?.wig_source) },
    { header: "Wig Code", icon: <BookmarkRoundedIcon color="basic" fontSize="small" />, info: ticket?.wig_id },
    { header: "SKU", icon: <StyleRoundedIcon color="basic" fontSize="small" />, info: <SimpleChip label={ticket?.sku} size="small" uc={true} /> },
    { header: "Sold On", info: formatGenericDate(ticket?.date_sold, 'short') },
  ]

  let eta;
  if (ticket?.status === "scheduled") {
    eta = DateTime.local().plus({days: ticket?.estimated_time_to_complete}).toLocaleString()
  } else if (ticket?.status === "completed") {
    eta = DateTime.local().toLocaleString()
  } else {
    eta = DateTime.fromISO(ticket?.accepted_at).plus({days: ticket?.estimated_time_to_complete}).toLocaleString();
  }

  const repair_summary = [
    {
      header: "Created On",
      info: ticket?.created_at,
      dataType: "date"
    },
    {
      header: "No. of Repairs",
      info: ticket?.repairs?.length,
      dataType: "number"
    },
    {
      header: "Total Price",
      info: ticket?.total,
      dataType: "currency"
    },
    {
      header: "ETA",
      info: eta,
    },
  ]

  const repair_follow_up = [
    {
      header: "Scheduled ETA",
      info: eta,
      spread: "16rem"
    },
    {
      header: "Follow-Up Service",
      info: <SimpleChip label={ticket?.follow_up_service} palette="secondary" />,
      spread: "16rem"
    },
    {
      header: "Scheduled Appointment",
      info: formatGenericDate(ticket?.scheduled_appointment) || 'N/A',
      // dataType: "date",
      spread: "16rem"
    }
  ]
  
  return (
    <Grid container spacing={3}>
      <Grid size={{xs: 12, md: 6, lg: 4}}>

        <Grid container spacing={3}>
          <Grid size={{xs: 12}}>
            <Card variant="outlined">
              <CardHeader title="Repair Ticket Details" subheader={ticket?.repair_ticket_num} action={<BasicFlex gap="1.5rem"><StatusChip repair={ticket} />
              <IconButton LinkComponent={Link} to={`/repair_tickets/edit/${ticket?.repair_ticket_id}`} color="secondary" ><EditRoundedIcon /></IconButton>
              </BasicFlex>} />

              <CardContent sx={{pt: 1}}>

                <CardBlockContainer>
                  <CardBlockHeader>Customer Details</CardBlockHeader>
                  {cust_block.map((block) => (
                    <CardBlock key={block.header} {...block} />
                  ))}

                  <CardSpacer />

                  <CardBlockHeader>Wig Details</CardBlockHeader>
                  {wig_details.map((block) => (
                    <CardBlock key={block.header} {...block} />
                  ))}

                  <CardSpacer />

                  <CardBlockHeader>Repair Summary</CardBlockHeader>
                  {repair_summary.map((block) => (
                    <CardBlock key={block.header} {...block} />
                  ))}
                </CardBlockContainer>
              </CardContent>

            </Card>
          </Grid>
          <Grid size={{xs: 12}}>
            <Card variant="outlined">
              <CardHeader title="Repair List" sx={{pb: 0}} />
              <CardContent sx={{pt: 0}}>
                <ListHeader>
                <CardBlockHeader>List of Repairs</CardBlockHeader>
                <EditTasks ticket={ticket} dash={dash} />
                </ListHeader>
                <BasicFlex dir="column" gap="1rem">
                {ticket?.repairs?.map((repair) => (
                  <RepairTask key={repair?.id} repair={repair} active={repair?.repair_order === activeRepair} handleActiveRepair={handleActiveRepair} repairs={ticket?.repairs} />
                ))}
                </BasicFlex>
              </CardContent>
            </Card>
          </Grid>

        </Grid>

      </Grid>

      <Grid size={{xs: 12, md: 6, lg: 8}}>
        <BasicFlex gap="2rem" align="flex-start">
          <Card variant="outlined" sx={{minWidth: "40rem", marginBottom: 3}}>

            <CardHeader title="Repair Follow Up" />
            <CardContent>
              <CardBlockContainer>
                {repair_follow_up.map((block) => (
                  <CardBlock key={block.header} {...block} />
                ))}
              </CardBlockContainer>
            </CardContent>
            <Divider />
            <CardActions>
              <Button fullWidth variant="contained" color="secondary" 
              startIcon={<EventAvailableRoundedIcon />}
              onClick={() => scheduleFollowUp(eta)}
              disabled={ticket?.scheduled_appointment}
              >
              Schedule Follow-Up Appointment
              </Button>
            </CardActions>

          </Card>
          <ShareCard url={`/repair_tickets/generate/report/${ticket?.repair_ticket_id}`} defaultEmail={ticket?.customer?.email} title="Repair Ticket Actions" />
        </BasicFlex>

        <SelectedRepair selectedRepair={selectedRepair} ticket={ticket} />
        
      </Grid>

      

    </Grid>
    )
};

export default MainRepairView;